/**
 * Web Billing Page
 * 
 * Clean composition layer using the Facade pattern.
 * 
 * Architecture:
 * - Uses shared useBillingPageFacade from @ideoz/core (zero duplication!)
 * - Injects web-specific implementations (navigation, auth)
 * - Passes facade directly to BillingPageTemplate
 * 
 * Benefits:
 * - Single source of truth for logic
 * - Easy to test (mock one facade)
 * - Platform-agnostic template
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BillingPageTemplate, showToast } from '@ideoz/ui';
import {
  useAppSelector,
  useAppDispatch,
  selectUser,
  useBillingPageFacade,
  getUserProfile,
  getAccessToken,
} from '@ideoz/core';
import { useWebNavigation } from '../hooks/useWebNavigation';
import { useAuthDialog } from '../hooks/useAuthDialog';
import { useAccountSidebarNavItems } from '../hooks/useAccountSidebarNavItems';

export default function Billing(): JSX.Element {
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const navigation = useWebNavigation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Check for payment success/cancelled from URL params
  const paymentParam = searchParams.get('payment');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const urlCleanedRef = useRef(false);
  const hasFetchedUserDataRef = useRef(false);
  
  // Fetch latest user data when entering billing page
  useEffect(() => {
    if (hasFetchedUserDataRef.current) return;
    hasFetchedUserDataRef.current = true;
    
    const accessToken = getAccessToken();
    if (accessToken) {
      dispatch(getUserProfile(accessToken)).catch((error) => {
        console.error('Failed to refresh user data on billing page enter:', error);
      });
    }
  }, [dispatch]);
  
  // Handle payment URL params and clean up URL
  useEffect(() => {
    if (paymentParam && !urlCleanedRef.current) {
      urlCleanedRef.current = true;
      
      if (paymentParam === 'success') {
        setPaymentSuccess(true);
      } else if (paymentParam === 'cancelled') {
        showToast({
          message: 'Payment was cancelled. You can try again anytime.',
          severity: 'info',
        });
      }
      
      // Clean up URL params after reading
      // Use setTimeout to ensure the facade reads the state first
      setTimeout(() => {
        setSearchParams({}, { replace: true });
      }, 100);
    }
  }, [paymentParam, setSearchParams]);
  
  // Callback to refresh user profile after successful payment
  const handleRefreshUserProfile = useCallback(async () => {
    const accessToken = getAccessToken();
    if (accessToken) {
      await dispatch(getUserProfile(accessToken)).unwrap();
    }
  }, [dispatch]);

  // Adapt authDialog to AuthFacade interface
  const authDialog = useAuthDialog();
  const auth = React.useMemo(
    () => ({
      isOpen: authDialog.isOpen,
      mode: authDialog.mode,
      isLoading: authDialog.isLoading,
      openLogin: authDialog.openLogin,
      openRegister: authDialog.openRegister,
      close: authDialog.closeDialog,
    }),
    [authDialog]
  );

  // ========== Shared Facade (100% Reusable) ==========
  const facade = useBillingPageFacade({
    navigation,
    auth,
    showToast,
    onRefreshUserProfile: handleRefreshUserProfile,
    paymentSuccess,
  });

  // Account-level page: show the account nav (My Startups / Profile / Billing /
  // App Settings), not the startup-scoped nav — the latter targets a specific
  // startup and is meaningless here at the root.
  const accountNavItems = useAccountSidebarNavItems('billing');
  const facadeWithStartupSidebar = React.useMemo(
    () => ({
      ...facade,
      sidebar: { ...facade.sidebar, navigationItems: accountNavItems },
    }),
    [facade, accountNavItems]
  );

  // Guard against null user
  if (!user) {
    return <div>Loading...</div>;
  }

  // ========== Render ==========
  return (
    <BillingPageTemplate
      facade={facadeWithStartupSidebar}
      isWeb={true}
      authDialogState={authDialog}
    />
  );
}
